import DesignTokens from '@tableau/design-tokens';
import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

import { LoadingPoolsSpinner } from '../../bridgeMultiPool/LoadingPoolsSpinner';
import { RoutingFilterItem } from '../../types/RoutingFilterItem';
import { useRoutingFiltersActions } from '../RoutingFiltersActionsContext';
import { useRoutingFiltersConflict } from '../RoutingFiltersConflictContext';
import { RoutingFilterTableHeader } from '../routingFilterTable/RoutingFilterTableHeader';
import { RoutingFilterTableRow } from '../routingFilterTable/RoutingFilterTableRow';
import { FilterGroup, GroupedRoutingFilterViewProps } from './GroupedRoutingFilterViewTypes';
import { useAutoExpandGroups } from './useAutoExpandGroups';

// Re-exported so existing importers (the pool/domain/permission views and tests)
// can keep importing these from the component module. Defined in
// GroupedRoutingFilterViewTypes to break the view↔hook dependency cycle.
export { FilterGroup, GroupedRoutingFilterViewProps } from './GroupedRoutingFilterViewTypes';

function GroupHeader({ groupHeaderLabel, countHeaderLabel }: { groupHeaderLabel: string, countHeaderLabel: string }) {
  return (
    <thead>
      <tr>
        <th className={headerCellClass + ' ' + headerSmallWidthClass}>
          <span className={visuallyHiddenClass}>{Messages.expand()}</span>
        </th>
        <th className={headerCellClass}>
          <span className={'tb-data-grid-header-text'}>{groupHeaderLabel}</span>
        </th>
        <th className={headerCellClass}>
          <span className={'tb-data-grid-header-text'}>{countHeaderLabel}</span>
        </th>
        <th className={headerCellClass}>
          <span className={'tb-data-grid-header-text'}>{Messages.actions()}</span>
        </th>
      </tr>
    </thead>
  );
}

export function GroupedRoutingFilterView(props: GroupedRoutingFilterViewProps) {
  const { groupHeaderLabel, countHeaderLabel, groups, isLoading, testIdPrefix } = props;
  const { onAddFilterClicked } = useRoutingFiltersActions();
  const { unsavedFilter, conflictsHighlighted } = useRoutingFiltersConflict();

  // Which groups qualify for conflict/unsaved auto-expansion right now. This is
  // the routing-filter-specific policy; useAutoExpandGroups applies it generically.
  const conflictExpandKeys = React.useMemo(() => {
    const keys = new Set<string>();
    if (unsavedFilter) {
      const unsavedId = unsavedFilter.filter.id;
      const conflictIds = unsavedFilter.conflictingFilterIds;
      for (const group of groups) {
        const hasUnsaved = group.filters.some(f => f.id === unsavedId);
        // Conflicting groups only auto-expand once the user reveals conflicts via
        // the badge on the unsaved row, matching the row-highlight behavior.
        const hasConflicting = conflictsHighlighted && group.filters.some(f => conflictIds.has(f.id));
        if (hasUnsaved || hasConflicting) {
          keys.add(group.key);
        }
      }
    }
    return keys;
  }, [unsavedFilter, groups, conflictsHighlighted]);

  const { expandedKeys, toggleGroup } = useAutoExpandGroups({ groups, isLoading, conflictExpandKeys });

  if (isLoading) {
    return (
      <table className={outerTableClass}>
        <GroupHeader groupHeaderLabel={groupHeaderLabel} countHeaderLabel={countHeaderLabel} />
        <LoadingPoolsSpinner />
      </table>
    );
  }

  if (groups.length === 0) {
    return (
      <div className={noFiltersClass}>{Messages.bridgeSiteSettingsNoRoutingFiltersSpecified()}</div>
    );
  }

  return (
    <table className={outerTableClass}>
      <GroupHeader groupHeaderLabel={groupHeaderLabel} countHeaderLabel={countHeaderLabel} />
      {groups.map((group: FilterGroup) => {
        const isExpanded = expandedKeys.has(group.key);
        const cellClass = 'tb-data-grid-separator-row' + (isExpanded ? ' ' + expandedRowClass : '');
        return (
          <tbody key={group.key}>
            <tr>
              <td className={expandCellClass + ' ' + cellClass}>
                <ButtonWidget
                  buttonType={ButtonType.IconOutline}
                  testId={`expand-${testIdPrefix}-${group.key}`}
                  onClick={() => toggleGroup(group.key)}
                  disabled={group.filters.length === 0}
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? Messages.collapse() : Messages.expand()} ${group.label}`}
                >
                  {isExpanded
                    ? <img src={imgPath('arrow_chevron_up_14px-small.svg')} alt={Messages.collapse()} />
                    : <img src={imgPath('arrow_chevron_down_14px-small.svg')} alt={Messages.expand()} />
                  }
                </ButtonWidget>
              </td>
              <td className={cellClass}>
                <span className={'tb-data-grid-cell-text tb-lr-padded-wide ' + groupLabelClass}>{group.label}</span>
              </td>
              <td className={cellClass}>
                <span className={'tb-data-grid-cell-text tb-lr-padded-wide'}>{group.filters.length}</span>
              </td>
              <td className={cellClass + ' ' + actionCellClass}>
                <ButtonWidget
                  buttonType={ButtonType.LowEmphasis}
                  testId={`add-filter-${testIdPrefix}-${group.key}`}
                  onClick={() => onAddFilterClicked(group.preFill)}
                  disabled={!!unsavedFilter}
                >
                  {`+ ${Messages.bridgeSiteSettingsAddRoutingFilter()}`}
                </ButtonWidget>
              </td>
            </tr>
            {isExpanded && group.filters.length > 0 && (
              <tr className={expandedContentClass}>
                <td className={'tb-data-grid-separator-row'} />
                <td colSpan={3}>
                  <table className={innerTableClass}>
                    <RoutingFilterTableHeader />
                    <tbody>
                      {group.filters.map((filter: RoutingFilterItem) => (
                        <RoutingFilterTableRow
                          key={filter.id}
                          filter={filter}
                        />
                      ))}
                    </tbody>
                  </table>
                </td>
              </tr>
            )}
          </tbody>
        );
      })}
    </table>
  );
}

const outerTableClass = cssClass('grouped-routing-filter', 'outer-table', {
  width: '100%',
  borderSpacing: 0,
  textAlign: 'left',
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
  cursor: 'default',
});

const headerCellClass = cssClass('grouped-routing-filter', 'header-cell', {
  height: '37px',
  borderBottomWidth: '1px',
  borderBottomColor: applyColor(DesignTokens.color.elevation.border.default.subtle, '#e0e0e0'),
  borderBottomStyle: 'solid',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
});

const headerSmallWidthClass = cssClass('grouped-routing-filter', 'header-small-width', {
  width: '35px',
});

const expandCellClass = cssClass('grouped-routing-filter', 'expand-cell', {
  width: 40,
  textAlign: 'center',
});

const actionCellClass = cssClass('grouped-routing-filter', 'action-cell', {
  textAlign: 'right',
  whiteSpace: 'nowrap',
});

const expandedRowClass = cssClass('grouped-routing-filter', 'expanded-row', {
  backgroundColor: applyColor(DesignTokens.color.component.background.header.default.info, '#e8f4fd'),
});

const innerTableClass = cssClass('grouped-routing-filter', 'inner-table', {
  width: '100%',
  borderCollapse: 'collapse',
  textAlign: 'left',
  marginBottom: TabStyles.Spacing.DefaultSpace,
});

const expandedContentClass = cssClass('grouped-routing-filter', 'expanded-content', {
  background: 'transparent',
  border: '1px solid ' + applyColor(DesignTokens.color.utility.focus.default.default, '#3498DB'),
  boxShadow: '0 0 4px 0 ' + applyColor(DesignTokens.color.utility.focus.default.default, '#3498DB'),
  borderRadius: '1px',
});

const noFiltersClass = cssClass('grouped-routing-filter', 'no-filters', {
  textAlign: 'center',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

const groupLabelClass = cssClass('grouped-routing-filter', 'group-label', {
  fontWeight: 'bold',
});

const visuallyHiddenClass = cssClass('grouped-routing-filter', 'visually-hidden', {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
});
